/**
 * **Covering Fire's reaction shot** (`t_heavy_gunner:1`, Core p.97): "if your target moves more
 * than 1 space, they clear Impaired, but you may attack them as a reaction with a HEAVY ranged
 * weapon for half damage, Heat, or Burn". lancer-data files it as its own 1/round reaction
 * ("Covering Fire Attack"), separate from Overwatch.
 *
 * The shot is a granted reaction shot (`reactionShot.ts`'s `REACTION_SHOT_GRANTS` row): the target
 * carries a `Covering Fire Mark` sourced from the attacker, and the weapon that would fire is a
 * Heavy ranged weapon; it spends `covering_fire` and is halved. Any other reaction attack at the
 * marked target stays Overwatch.
 *
 * Known ambiguity: an Overwatch fired with a non-Ordnance Heavy ranged weapon at a target that
 * carries the attacker's Mark reads as the Covering Fire shot. Beakman's only Heavy ranged weapon
 * is Ordnance, which can never Overwatch, so the case needs another build to arise.
 */

import type { EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import { isItemDestroyed, isRangedAttackAt, primaryReach, weaponInstanceKey } from "@lancer-vtt/domain";
import { err, ok } from "@lancer-vtt/shared";
import { combatantRange } from "../effects/spatialAnswerer";
import { checkLineOfSight } from "../search";
import type { BudgetVerdict } from "./types";

/** The marker status `t_heavy_gunner:1` plants on its target (`entries/statuses/covering-fire-mark.ts`). */
export const COVERING_FIRE_MARK_STATUS = "Covering Fire Mark";
/** The named 1/round reaction the shot spends — the Mark's optional move clause's `name`. */
export const COVERING_FIRE_REACTION = "covering_fire";
/** The Covering Fire quick action's affordance id (the talent rank's pilot-effect row). */
export const COVERING_FIRE_ACTION = "t_heavy_gunner:1";
/** RAW's own cap on the cast: "and within 10 spaces". */
const COVERING_FIRE_MAX_REACH = 10;
/** The weapon size string a Heavy weapon declares in its frozen `mount`. */
const WEAPON_SIZE_HEAVY = "Heavy";

/** Is `w` a Heavy ranged weapon — the only kind Covering Fire's reaction may fire? */
export function isHeavyRangedWeapon(w: WeaponProfile): boolean {
  return w.mount === WEAPON_SIZE_HEAVY && isRangedAttackAt(w.range, null);
}

/** Does `target` carry a Covering Fire Mark that `attacker` planted? */
export function hasCoveringFireMarkFrom(state: EncounterState, args: { attacker: string; target: string }): boolean {
  return state.combatants[args.target]?.statuses.some((s) => s.name === COVERING_FIRE_MARK_STATUS && s.source === args.attacker) === true;
}

/**
 * May `actor` cast Covering Fire at `target`? RAW (Core p.97): "choose a character within line of
 * sight and RANGE of one of your HEAVY ranged weapons, and within 10 spaces". The reach is the
 * longest Range among the actor's undestroyed Heavy ranged weapons, capped at 10, measured live
 * (not frozen at spawn), so a destroyed weapon stops counting and a live unit needs no respawn.
 * With no such weapon the cast is refused outright: there is no legal target. Any other action,
 * or a cast with no target named, passes (the target-less case is the resolver's to refuse).
 */
export function checkCoveringFireCast(args: { state: EncounterState; actor: string; action: string; target?: string }): BudgetVerdict {
  const { state, actor, action, target } = args;
  if (action !== COVERING_FIRE_ACTION) return ok(undefined);
  const act = state.combatants[actor];
  if (!act) return err(`covering fire: ${actor} is not on the map`);
  const heavy = (act.base.weapons ?? []).filter((w) => isHeavyRangedWeapon(w) && !isItemDestroyed(act.current.resources, weaponInstanceKey(w)));
  if (heavy.length === 0) return err(`covering fire: ${act.name} has no working Heavy ranged weapon — the target must be within Range of one (Core p.97)`);
  if (target === undefined || target === actor) return ok(undefined);
  const tgt = state.combatants[target];
  if (!tgt) return err(`covering fire: ${target} is not on the map`);
  const reach = Math.min(COVERING_FIRE_MAX_REACH, Math.max(...heavy.map((w) => primaryReach(w.range ?? []))));
  const range = combatantRange(state, actor, target);
  if (range === null) return err(`covering fire: ${act.name} or ${tgt.name} is not placed on the map (can't measure range)`);
  if (range > reach) {
    return err(`covering fire: ${tgt.name} is ${range} away — outside Range ${reach} (the longest Heavy ranged weapon's Range, at most 10)`);
  }
  return checkLineOfSight({ state, viewer: actor, target, verb: "covering fire" });
}
